import React from 'react';
import { motion } from 'framer-motion';
import SectionHeading from '../ui/SectionHeading';
import ScrollReveal from '../ui/ScrollReveal';

const InsightsSection = () => {
  const insights = [
    {
      title: 'The Future of Crude Oil Markets in a Transitioning Energy Landscape',
      date: 'June 5, 2023',
      category: 'Market Analysis',
      excerpt: 'As the world gradually shifts toward renewable energy sources, traditional crude oil markets are adapting to maintain relevance and value in an evolving global economy.',
      image: 'https://images.unsplash.com/photo-1610881378556-6a8dd64b6ba7'
    },
    {
      title: 'EN590 Diesel: Meeting Evolving Regulatory Standards in European Markets',
      date: 'April 18, 2023',
      category: 'Product Insights',
      excerpt: 'Recent changes to European emissions standards are reshaping the EN590 diesel market, creating new challenges and opportunities for traders and suppliers.',
      image: 'https://images.unsplash.com/photo-1513295202663-54cd69dd2b43'
    },
    {
      title: 'Navigating Volatility: Risk Management Strategies for Fuel Traders',
      date: 'February 22, 2023',
      category: 'Trading Strategy',
      excerpt: 'In increasingly uncertain markets, sophisticated risk management approaches are becoming essential for hydrocarbon traders seeking to protect margins and exploit opportunities.',
      image: 'https://images.unsplash.com/photo-1462396240927-52058a6a84ec'
    }
  ];

  return (
    <section id="insights" className="section-padding relative bg-black">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>
      
      {/* Gradient accent */}
      <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[600px] h-[600px] rounded-full bg-accent-amber/10 blur-[100px] pointer-events-none"></div>
      
      <div className="container-custom relative z-10">
        <SectionHeading
          subtitle="Market Insights"
          title="Industry Expertise & Analysis"
          description="Stay informed with our latest perspectives on energy markets, regulatory developments, and trading strategies that impact the hydrocarbon industry."
        />
        
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          {insights.map((insight, index) => (
            <ScrollReveal key={index} delay={0.1 * index}>
              <motion.div 
                className="glass-card overflow-hidden group h-full flex flex-col"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="h-48 md:h-56 overflow-hidden relative">
                  <div
                    className="absolute inset-0 bg-cover bg-center group-hover:scale-110 transition-transform duration-700 ease-in-out"
                    style={{ backgroundImage: `url(${insight.image})` }}
                  ></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-850 to-transparent opacity-60"></div>
                  
                  <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center">
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-white/10 backdrop-blur-sm text-white">
                      {insight.category}
                    </span>
                    <span className="text-sm text-gray-300">{insight.date}</span>
                  </div>
                </div>
                
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold mb-3 group-hover:text-primary-400 transition-colors">
                    {insight.title}
                  </h3>
                  <p className="text-gray-300 mb-5 flex-grow">
                    {insight.excerpt}
                  </p>
                  <div className="mt-auto">
                    <a 
                      href="#contact"
                      className="inline-flex items-center text-accent-gold hover:text-white transition-colors group-hover:translate-x-2 transition-transform duration-300"
                    >
                      Read more
                      <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path 
                          d="M5 12H19M19 12L12 5M19 12L12 19" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
              </motion.div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InsightsSection;